import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// ✅ Consistent endpoint for the Module API
const MODULE_BASE_URL = `${API_BASE_PATH}/productmodules/`;

// ✅ Module Interface
export interface Module {
  module_id?: number;
  module_name: string;
  description: string;
  product_id: number;
  product?: {
    product_id: number;
    product_name: string;
    description: string;
  };
  created_at?: string;
  updated_at?: string;
}

// ✅ Fetch all modules
export const fetchModules = async (token: string | null): Promise<Module[]> => {
  try {
    const response = await axios.get(MODULE_BASE_URL, {
      headers: {
        authorization: token ? `Bearer ${token}` : "",
      },
    });
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// ✅ Create a new module
export const createModule = async (
  token: string | null,
  moduleData: Module
): Promise<Module | null> => {
  try {
    const formData = new URLSearchParams();
    formData.append("module_name", moduleData.module_name);
    formData.append("description", moduleData.description);
    formData.append("product_id", moduleData.product_id.toString());

    const response = await axios.post(MODULE_BASE_URL, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        authorization: token ? `Bearer ${token}` : "",
      },
    });
    toast.success("Module added successfully");

    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// ✅ Update an existing module
export const updateModule = async (
  token: string | null,
  moduleId: number,
  moduleData: Module
): Promise<Module | null> => {
  try {
    const formData = new URLSearchParams();
    formData.append("module_name", moduleData.module_name);
    formData.append("description", moduleData.description);
    formData.append("product_id", moduleData.product_id.toString());

    const response = await axios.put(
      `${MODULE_BASE_URL}/${moduleId}`,
      formData,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    toast.success("Module updated successfully");
    return response.data;
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};

// ✅ Delete a module
export const deleteModule = async (
  token: string | null,
  moduleId: number
): Promise<void> => {
  try {
    await axios.delete(`${MODULE_BASE_URL}/${moduleId}`, {
      headers: {
        authorization: token ? `Bearer ${token}` : "",
      },
    });
    toast.success("Module deleted successfully");
  } catch (error: any) {
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};
